import { useState, type ReactNode } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { DateTimeRangePicker, Tag } from '@/components';
import { ChipPopover } from '@/components/FilterBar';
import {
    DateTimeMode,
    getDateTimeLabel,
    type DateTimeFilter,
} from '@/utils/query-filters';

interface WebhookDeliveryFiltersProps {
    dateTime: DateTimeFilter;
    resolvedRange: { startDate: Date; endDate: Date } | null;
    onDateTimeChange: (next: DateTimeFilter) => void;
    hint?: ReactNode;
}

export function WebhookDeliveryFilters({
    dateTime,
    resolvedRange,
    onDateTimeChange,
    hint,
}: WebhookDeliveryFiltersProps) {
    const [dateOpen, setDateOpen] = useState(false);

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            <ChipPopover
                popoverOpen={dateOpen}
                onPopoverOpenChange={setDateOpen}
                chip={
                    <Tag
                        size="sm"
                        leadingIcon={<CalendarBlankIcon />}
                        onClick={() => setDateOpen(!dateOpen)}
                        onRemove={
                            dateTime.mode !== DateTimeMode.ALL
                                ? () =>
                                      onDateTimeChange({
                                          mode: DateTimeMode.ALL,
                                      })
                                : undefined
                        }
                    >
                        {getDateTimeLabel(dateTime)}
                    </Tag>
                }
            >
                <DateTimeRangePicker
                    initialStartDate={resolvedRange?.startDate}
                    initialEndDate={resolvedRange?.endDate}
                    initialMode={dateTime.mode}
                    immediatelyApply
                    onApply={(range) => {
                        if (range.startTime && range.endTime) {
                            onDateTimeChange({
                                mode: range.mode,
                                customRange: {
                                    startDate: range.startTime,
                                    endDate: range.endTime,
                                },
                            });
                        }
                    }}
                    onClear={() => onDateTimeChange({ mode: DateTimeMode.ALL })}
                    onClose={() => setDateOpen(false)}
                />
            </ChipPopover>
            {hint}
        </div>
    );
}
